import type { DisplayToast } from '@terminal/components/toasts-layout/ToastItem';
import { ToastItem } from '@terminal/components/toasts-layout/ToastItem';
import { offsetBetweenToasts } from '@terminal/components/toasts-layout/utils';
import { useToasts } from '@terminal/stores/use-toasts/useToasts';
import { createRef, RefObject, useRef } from 'react';

import styles from './ToastsLayout.module.scss';

export const CONVERSATION_TOAST = 'CONVERSATION_TOAST';

const SCOPES_TO_CLEAR = [CONVERSATION_TOAST];

export const ToastsLayout = () => {
  const { toasts, clearScopedToasts } = useToasts(state => ({
    toasts: state.toasts,
    clearScopedToasts: state.clearScopedToasts,
  }));

  const refs = useRef<Record<string, RefObject<HTMLDivElement | null>>>({});

  toasts.forEach(t => {
    refs.current[t.id] = refs.current[t.id] || createRef();
  });

  const toastIds = new Set(toasts.map(t => String(t.id)));
  Object.keys(refs.current).forEach(id => {
    if (!toastIds.has(id)) {
      delete refs.current[id];
    }
  });

  const displayToasts: DisplayToast[] = toasts.map((toast, i) => ({
    toast,
    exiting: !!toast.exiting,
    displayIndex: i,
  }));

  const yOffsets = displayToasts.map((_dt, toastIndex) =>
    displayToasts.slice(0, toastIndex).reduce((offsetY, dt) => {
      const ref = refs.current[dt.toast.id];
      const curr = displayToasts[toastIndex];
      if (ref?.current && curr && dt.toast.position === curr.toast.position) {
        return offsetY + ref.current.clientHeight + offsetBetweenToasts;
      }
      return offsetY;
    }, 0)
  );

  const filteredScopes = SCOPES_TO_CLEAR.filter(scope =>
    toasts.some(toast => toast.scope === scope)
  );

  const handleScopedToastsClose = () => {
    filteredScopes.forEach(scope => clearScopedToasts(scope));
  };

  const handleClick = filteredScopes.length
    ? () => handleScopedToastsClose()
    : undefined;

  return (
    <div
      className={styles.toastsWrapper}
      data-testid="toasts-wrapper"
      style={{ pointerEvents: handleClick ? 'all' : 'none' }}
      onClick={handleClick}
    >
      {displayToasts.map((displayToast, toastIndex) => (
        <ToastItem
          key={displayToast.toast.id}
          ref={refs.current[displayToast.toast.id]}
          displayToast={displayToast}
          offsetY={yOffsets[toastIndex]}
        />
      ))}
    </div>
  );
};
